/**
 * Financial Summary Builder
 *
 * Builds the financial summary payload for scenario persistence.
 * Handles both complete financial analysis and partial states (when analysis is pending).
 *
 * @module utils/financialSummaryBuilder
 */

import { debug } from '@vortx/shared-ui';

/**
 * Build capex breakdown from budget calculations
 *
 * @param {Object} budget - Budget state with capexCalculations and itcCalculations
 * @returns {Object|null} CapEx breakdown object or null if not available
 */
export function buildCapexBreakdown(budget) {
    if (!budget?.capexCalculations) {
        return null;
    }

    const components = budget.capexCalculations.components;

    return {
        solar: components?.pvTurnkey?.total || 0,
        storage: (components?.battery?.equipment || 0) +
                (components?.battery?.installation || 0),
        generator: (components?.generator?.equipment || 0) +
                  (components?.generator?.installation || 0),
        bos: budget.capexCalculations.softCostsTotal || 0,
        installation: budget.capexCalculations.installation || 0,
        total: budget.capexCalculations.total || 0,
        itc_amount: budget.itcCalculations?.totalItc || 0,
        net_capex: budget.itcCalculations?.netCostAfterItc || budget.capexCalculations.total || 0,
    };
}

/**
 * Build cash flows array from financial analysis
 *
 * @param {Object} financialAnalysis - Financial analysis with projectCashflows
 * @param {number} maxYears - Maximum years to include (default 25)
 * @returns {Array|null} Cash flows array or null if not available
 */
export function buildCashFlowsArray(financialAnalysis, maxYears = 25) {
    const flows = financialAnalysis?.projectCashflows?.slice(0, maxYears);
    if (!flows) return null;

    const initialInvestment = financialAnalysis.summary?.initialInvestment || 0;
    let cumulative = -initialInvestment;

    return flows.map(cf => {
        cumulative += (cf.netCashflow ?? 0);
        return {
            year: cf.year,
            savings: cf.energySavings ?? 0,
            om_costs: cf.omCost ?? 0,
            net_cashflow: cf.netCashflow ?? 0,
            cumulative: cumulative,
        };
    });
}

/**
 * Build complete financial summary for scenario persistence
 *
 * Handles three cases:
 * 1. Full financial analysis available - builds complete summary
 * 2. No analysis but budget available - builds partial with current capex
 * 3. Neither available - returns saved summary or null
 *
 * @param {Object} options
 * @param {Object} options.financialAnalysis - Computed financial analysis (may be null)
 * @param {Object} options.budget - Budget state with calculations
 * @param {Object} options.savedFinancialSummary - Previously saved summary (fallback)
 * @returns {Object|null} Financial summary for persistence
 */
export function buildFinancialSummary({ financialAnalysis, budget, savedFinancialSummary }) {
    // ALWAYS use current budget for capex_breakdown, even if financialAnalysis not ready
    // This prevents stale capex from being saved when user saves before monthly data loads
    const currentCapexBreakdown = buildCapexBreakdown(budget) ||
                                   savedFinancialSummary?.capex_breakdown ||
                                   null;

    if (financialAnalysis) {
        const newSummary = {
            npv: financialAnalysis.summary?.npv ?? null,
            paybackPeriod: financialAnalysis.summary?.paybackPeriod ?? null,
            totalSavings: financialAnalysis.summary?.totalSavings ?? null,
            initialInvestment: financialAnalysis.summary?.initialInvestment ?? null,
            // Year 1 savings: energy savings (raw) and net savings (after fuel, standby, O&M)
            annualSavings: financialAnalysis.projectCashflows?.[0]?.energySavings ?? null,
            year1NetSavings: financialAnalysis.projectCashflows?.[0]?.netCashflow ?? null,
            // BAU utility cost (Year 1) - needed for effective rate and rate discount calculations
            bauYearOneUtilityCost: financialAnalysis.projectCashflows?.[0]?.bauUtilityCost ?? null,
            // Include CapEx breakdown for scenario comparison - ALWAYS from current budget
            capex_breakdown: currentCapexBreakdown,
            // Include cash flows for scenario comparison (limit to 25 years)
            cash_flows: buildCashFlowsArray(financialAnalysis),
        };

        debug.log('📊 [financialSummaryBuilder] Computed new financialSummary:', newSummary);
        return newSummary;
    }

    // Financial analysis not ready (waiting for monthly data) - use partial data
    // CRITICAL: Always use current budget capex, preserve other metrics from previous save
    const partialSummary = {
        ...savedFinancialSummary,
        // ALWAYS override capex with current budget values
        capex_breakdown: currentCapexBreakdown,
    };

    debug.log('📊 [financialSummaryBuilder] Using partial financialSummary (analysis not ready, but capex from current budget):', partialSummary);
    return partialSummary;
}

export default buildFinancialSummary;
